import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Shield, Scale, FileText, DollarSign, AlertTriangle, Calendar, Download, Upload, User, Users, CheckCircle, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format, addMonths, addDays } from "date-fns";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { formatEINForDisplay } from "@/lib/ein";

export default function ERISADetails() {
  const [location, setLocation] = useLocation();

  const { data: employeesData = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  const employees = useMemo(() => {
    return sortEmployeesByFullName(employeesData);
  }, [employeesData]);

  const { data: plans = [] } = useQuery<any[]>({
    queryKey: ["/api/plans"],
  });

  // Generate ERISA compliance data
  const erisaData = {
    planInfo: {
      planYear: '2024',
      planName: 'TechCorp Employee Welfare Benefit Plan',
      ein: '12-3456789',
      planNumber: '501',
      participants: employees.length,
      eligibleParticipants: Math.floor(employees.length * 0.85),
      planAssets: 2450000,
      totalContributions: 485000,
      totalBenefits: 367000
    },
    fiduciaries: [
      {
        id: 1,
        name: 'Sarah Johnson',
        title: 'Plan Administrator',
        type: 'Named Fiduciary',
        responsibilities: ['Plan Administration', 'Claims Processing', 'Participant Communication'],
        bondAmount: 100000,
        bondExpiry: new Date('2024-12-31')
      },
      {
        id: 2,
        name: 'Michael Chen',
        title: 'CFO',
        type: 'Investment Fiduciary',
        responsibilities: ['Investment Selection', 'Fee Monitoring', 'Performance Review'],
        bondAmount: 150000,
        bondExpiry: new Date('2024-12-31')
      },
      {
        id: 3,
        name: 'ABC Benefits Consulting',
        title: 'Third Party Administrator',
        type: 'Service Provider',
        responsibilities: ['Claims Administration', 'COBRA Administration', 'Compliance Support'],
        bondAmount: 500000,
        bondExpiry: new Date('2025-06-30')
      }
    ],
    documents: {
      spd: {
        lastUpdated: new Date('2024-01-01'),
        distributed: new Date('2024-01-15'),
        nextUpdate: new Date('2025-01-01'),
        status: 'Current'
      },
      form5500: {
        dueDate: new Date('2024-07-31'),
        filed: false,
        preparedBy: 'Internal',
        status: 'In Progress'
      },
      sars: [
        {
          year: 2023,
          distributed: new Date('2024-03-15'),
          method: 'Electronic',
          participants: employees.length - 5
        }
      ]
    },
    compliance: {
      lastAudit: new Date('2023-08-15'),
      nextAudit: new Date('2024-08-15'),
      findings: 2,
      corrected: 1,
      pending: 1,
      bondingCompliant: true,
      fidelityBondAmount: 500000,
      requiredBondAmount: 485000
    }
  };

  // Generate participant data with ERISA requirements
  const participantData = employees.slice(0, 20).map((employee: any) => {
    const seed = employee.id * 2468;
    const enrollmentDate = new Date('2024-01-01');
    enrollmentDate.setDate(enrollmentDate.getDate() + (seed % 90));
    
    const eligibilityDate = new Date(employee.hireDate || '2024-01-01');
    eligibilityDate.setDate(eligibilityDate.getDate() + 60); // 60-day waiting period
    
    const isEligible = new Date() >= eligibilityDate;
    const isEnrolled = isEligible && (seed % 5) !== 0; // 80% enrollment rate
    
    const annualSalary = 45000 + (seed % 55000);
    const employerContribution = annualSalary * 0.08; // 8% employer contribution
    const employeeContribution = isEnrolled ? annualSalary * 0.04 : 0; // 4% employee contribution
    
    return {
      ...employee,
      eligibilityDate,
      enrollmentDate: isEnrolled ? enrollmentDate : null,
      isEligible,
      isEnrolled,
      annualSalary,
      employerContribution,
      employeeContribution,
      totalContribution: employerContribution + employeeContribution,
      vestingPercentage: Math.min(100, Math.max(0, (seed % 6) * 20)), // 0-100% vesting
      claimsThisYear: seed % 3,
      spdReceived: (seed % 10) !== 0, // 90% received SPD
      sarReceived: (seed % 8) !== 0 // 87.5% received SAR
    };
  });

  const eligibleParticipants = participantData.filter(p => p.isEligible);
  const enrolledParticipants = participantData.filter(p => p.isEnrolled);
  const pendingSPD = participantData.filter(p => !p.spdReceived);
  const pendingSAR = participantData.filter(p => !p.sarReceived);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/compliance?role=employer")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Compliance
          </Button>
          <h1 className="text-3xl font-bold">ERISA Compliance Management</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Reports
          </Button>
          <Button size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* ERISA Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            ERISA Plan Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {enrolledParticipants.length}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Active Participants
              </div>
              <div className="text-xs text-gray-500 mt-1">
                of {eligibleParticipants.length} eligible
              </div>
            </div>
            
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                ${(erisaData.planInfo.planAssets / 1000000).toFixed(1)}M
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Plan Assets
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Total plan value
              </div>
            </div>

            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {erisaData.compliance.pending}
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Pending Issues
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Compliance findings
              </div>
            </div>

            <div className="text-center p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                Jul 31
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Form 5500 Due
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Annual filing deadline
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="plan" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="plan">Plan Information</TabsTrigger>
          <TabsTrigger value="fiduciaries">Fiduciaries</TabsTrigger>
          <TabsTrigger value="participants">Participants</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        {/* Plan Information Tab */}
        <TabsContent value="plan" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Plan Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plan Name</Label>
                    <Input value={erisaData.planInfo.planName} readOnly />
                  </div>
                  <div className="space-y-2">
                    <Label>Plan Year</Label>
                    <Input value={erisaData.planInfo.planYear} readOnly />
                  </div>
                  <div className="space-y-2">
                    <Label>EIN</Label>
                    <Input value={formatEINForDisplay(erisaData.planInfo.ein)} readOnly />
                  </div>
                  <div className="space-y-2">
                    <Label>Plan Number</Label>
                    <Input value={erisaData.planInfo.planNumber} readOnly />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Plan Type</Label>
                  <Select defaultValue="welfare">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="welfare">Employee Welfare Benefit Plan</SelectItem>
                      <SelectItem value="pension">Employee Pension Benefit Plan</SelectItem>
                      <SelectItem value="both">Combined Plan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Update Plan Document
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Plan Assets:</span>
                    <span className="text-sm font-bold">${erisaData.planInfo.planAssets.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Contributions:</span>
                    <span className="text-sm">${erisaData.planInfo.totalContributions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Benefits Paid:</span>
                    <span className="text-sm">${erisaData.planInfo.totalBenefits.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Net Assets:</span>
                    <span className="text-sm font-bold">
                      ${(erisaData.planInfo.planAssets - erisaData.planInfo.totalBenefits).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Participants:</span>
                    <span className="text-sm">{erisaData.planInfo.participants}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Eligible Participants:</span>
                    <span className="text-sm">{erisaData.planInfo.eligibleParticipants}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Fiduciaries Tab */}
        <TabsContent value="fiduciaries" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Plan Fiduciaries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Title</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Bond Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Bond Expiry</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {erisaData.fiduciaries.map((fiduciary: any) => {
                        const bondExpiring = fiduciary.bondExpiry < addMonths(new Date(), 3);
                        const bondStatus = bondExpiring ? 'expiring' : 'current';

                        return (
                          <tr key={fiduciary.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{fiduciary.name}</p>
                                <p className="text-xs text-muted-foreground">{fiduciary.title}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">{fiduciary.title}</td>
                            <td className="px-4 py-3">
                              <Badge variant="secondary" className="text-xs">
                                {fiduciary.type}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">${fiduciary.bondAmount.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm">
                              {format(fiduciary.bondExpiry, 'MMM dd, yyyy')}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={bondExpiring ? "destructive" : "default"} className="text-xs">
                                {bondExpiring ? 'Expiring Soon' : 'Current'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                    View Details
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Fiduciary Details</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div>
                                      <Label className="text-sm font-medium">Responsibilities</Label>
                                      <ul className="text-sm mt-1 space-y-1">
                                        {fiduciary.responsibilities.map((resp: string, idx: number) => (
                                          <li key={idx} className="text-muted-foreground">• {resp}</li>
                                        ))}
                                      </ul>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <Label className="text-sm font-medium">Bond Amount</Label>
                                        <p className="text-sm">${fiduciary.bondAmount.toLocaleString()}</p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium">Bond Expiry</Label>
                                        <p className="text-sm">{format(fiduciary.bondExpiry, 'MMM dd, yyyy')}</p>
                                      </div>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {erisaData.fiduciaries.length} fiduciaries registered
                </div>
                <Button>
                  <Shield className="h-4 w-4 mr-2" />
                  Add Fiduciary
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bonding Requirements */}
          <Card>
            <CardHeader>
              <CardTitle>Fidelity Bonding Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Required Bond Amount</Label>
                  <p className="text-lg font-bold text-green-600">
                    ${erisaData.compliance.requiredBondAmount.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    10% of plan assets (minimum $1,000, maximum $500,000)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Current Bond Amount</Label>
                  <p className="text-lg font-bold text-blue-600">
                    ${erisaData.compliance.fidelityBondAmount.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {erisaData.compliance.bondingCompliant ? 'Compliant' : 'Insufficient coverage'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Plan Participants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Participant</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Eligibility Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Enrollment Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Annual Contribution</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Vesting %</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">SPD Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participantData.slice(0, 15).map((participant: any) => {
                        const enrollmentStatus = participant.isEnrolled ? 'Enrolled' : 
                                               participant.isEligible ? 'Eligible' : 'Waiting';
                        const statusVariant = participant.isEnrolled ? 'default' : 
                                            participant.isEligible ? 'secondary' : 'outline';

                        return (
                          <tr key={participant.id} className="border-b hover:bg-muted/25">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{participant.firstName} {participant.lastName}</p>
                                <p className="text-xs text-muted-foreground">{participant.department || 'Unassigned'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {format(participant.eligibilityDate, 'MMM dd, yyyy')}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={statusVariant} className="text-xs">
                                {enrollmentStatus}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              ${participant.totalContribution.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">{participant.vestingPercentage}%</td>
                            <td className="px-4 py-3">
                              <Badge variant={participant.spdReceived ? "default" : "destructive"} className="text-xs">
                                {participant.spdReceived ? 'Received' : 'Pending'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                                View Details
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Required Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Summary Plan Description (SPD)</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {format(erisaData.documents.spd.lastUpdated, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Plan Document</p>
                      <p className="text-xs text-muted-foreground">
                        Governing plan document and amendments
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Trust Agreement</p>
                      <p className="text-xs text-muted-foreground">
                        Plan asset trust documentation
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Current</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>
                </div>

                <Button className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Filing Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Form 5500 (2024)</p>
                      <p className="text-xs text-muted-foreground">
                        Due: {format(erisaData.documents.form5500.dueDate, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">In Progress</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        Continue
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Summary Annual Report (SAR)</p>
                      <p className="text-xs text-muted-foreground">
                        2023 SAR distributed to {erisaData.documents.sars[0].participants} participants
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Complete</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        View
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Material Modifications</p>
                      <p className="text-xs text-muted-foreground">
                        Summary of Material Modifications (SMM)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">None Required</Badge>
                      <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                        Create
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Document Distribution Status */}
          <Card>
            <CardHeader>
              <CardTitle>Document Distribution Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>SPD Distribution</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${(participantData.filter(p => p.spdReceived).length / participantData.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm">
                      {Math.round((participantData.filter(p => p.spdReceived).length / participantData.length) * 100)}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {participantData.filter(p => p.spdReceived).length} of {participantData.length} participants
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>SAR Distribution</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${(participantData.filter(p => p.sarReceived).length / participantData.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm">
                      {Math.round((participantData.filter(p => p.sarReceived).length / participantData.length) * 100)}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {participantData.filter(p => p.sarReceived).length} of {participantData.length} participants
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Compliance Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Fiduciary Bonding:</span>
                    <Badge variant="default" className="text-xs">Compliant</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Form 5500 Filing:</span>
                    <Badge variant="secondary" className="text-xs">In Progress</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">SPD Distribution:</span>
                    <Badge variant="default" className="text-xs">90% Complete</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Participant Notices:</span>
                    <Badge variant="default" className="text-xs">Current</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Plan Audit:</span>
                    <Badge variant="secondary" className="text-xs">Due Soon</Badge>
                  </div>
                </div>

                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Overall Compliance:</span>
                    <Badge variant="default" className="text-xs">Good Standing</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Action Items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Complete Form 5500</p>
                    <p className="text-xs text-muted-foreground">Due: July 31, 2024</p>
                  </div>
                  <Badge variant="destructive" className="text-xs">High Priority</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Distribute SPD to New Hires</p>
                    <p className="text-xs text-muted-foreground">{pendingSPD.length} employees pending</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">Medium Priority</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Schedule Annual Plan Audit</p>
                    <p className="text-xs text-muted-foreground">Due: August 15, 2024</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">Medium Priority</Badge>
                </div>

                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Review Fiduciary Bonds</p>
                    <p className="text-xs text-muted-foreground">1 bond expiring within 90 days</p>
                  </div>
                  <Badge variant="outline" className="text-xs">Low Priority</Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Audit History */}
          <Card>
            <CardHeader>
              <CardTitle>Audit History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium">Audit Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Auditor</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Findings</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-muted/25">
                        <td className="px-4 py-3 text-sm">
                          {format(erisaData.compliance.lastAudit, 'MMM dd, yyyy')}
                        </td>
                        <td className="px-4 py-3 text-sm">Smith & Associates CPA</td>
                        <td className="px-4 py-3 text-sm">Annual Plan Audit</td>
                        <td className="px-4 py-3 text-sm">{erisaData.compliance.findings}</td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className="text-xs">1 Pending</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="outline" className="text-xs px-2 py-1">
                            View Report
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
